import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { IosInstallComponent } from '../components/ios-install/ios-install.component';
import { Context } from '../context';
import { AppEvents } from './app.events';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class InstallService {

  installed$ = new BehaviorSubject(false);

  deferredPrompt: Event | null;

  constructor(
    private platform: Platform,
    private modalController: ModalController,
    private notificationService: NotificationService,
    private appEvents: AppEvents
  ) { }

  init() {
    const mode = this.getPWADisplayMode();
    if (mode === 'browser') {
      window.addEventListener('appinstalled', () => this.appInstalled());
      window.addEventListener('beforeinstallprompt', (e) => this.beforeInstallPrompt(e));
    } else if (mode === 'standalone')
      this.installed$.next(true);
  }

  getPWADisplayMode() {
    const nav = navigator as any;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || !!('standalone' in nav && nav.standalone);

    if (document.referrer.startsWith('android-app://')) {
      return 'twa';
    } else if (isStandalone) {
      return 'standalone';
    }
    return 'browser';
  }

  appInstalled() {
    this.installed$.next(true);
    // Clear the deferredPrompt so it can be garbage collected
    this.deferredPrompt = null;
    // Optionally, send analytics event to indicate successful install
    this.appEvents.pwaEvent$.next({ type: 'installed' });
  }

  beforeInstallPrompt(e: Event) {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later.
    this.deferredPrompt = e;
    // Update UI notify the user they can install the PWA
    this.installed$.next(false);
    // Optionally, send analytics event that PWA install promo was shown.
    this.appEvents.pwaEvent$.next({ type: 'beforeinstallprompt' });
  }

  async install() {
    // Hide the app provided install promotion
    this.installed$.next(true);
    let deferredPrompt: any = this.deferredPrompt;
    if (!deferredPrompt) {
      this.appEvents.pwaEvent$.next({ type: 'error', data: 'User tried to install but deferredPrompt was null' })
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    // Optionally, send analytics event with outcome of user choice
    this.appEvents.pwaEvent$.next({ type: 'promptresponse', data: outcome });
    // We've used the prompt, and can't use it again, throw it away
    this.deferredPrompt = null;
  }

  async promptInstall() {
    if (this.platform.is('capacitor') || this.platform.is('cordova'))
      return;

    if (this.platform.is('ios')) {

      const modal = await this.modalController.create({
        component: IosInstallComponent,
        showBackdrop: false,
        cssClass: 'ios-install-modal',
        backdropDismiss: true,
        keyboardClose: true,
      });

      await modal.present();

      await modal.onDidDismiss();

    } else {

      try {
        this.install();
      } catch (err) {
        const res = await this.notificationService.confirm('Install App',
          `Install ${Context.brand?.title} on your home screen for quick access?`);

        if (res)
          await this.install();
      }
    }
  }
}
